// Insurance Module Theme Extensions
// Extends Koodooka theme system with insurance-specific tokens

import { theme } from 'antd';

// Insurance semantic colors following Koodooka patterns
export const insuranceColors = {
    // Quote Status Colors
    colorQuoteNew: '#1890ff',
    colorQuoteReferred: '#faad14',
    colorQuoteApproved: '#52c41a',
    colorQuoteDeclined: '#ff4d4f',
    colorQuoteExpired: '#d9d9d9',
    colorQuoteConverted: '#13c2c2',

    // Policy Status Colors
    colorPolicyActive: '#52c41a',
    colorPolicyLapsed: '#faad14',
    colorPolicyCancelled: '#ff4d4f',
    colorPolicyExpired: '#d9d9d9',
    colorPolicySuspended: '#fa8c16',
    colorPolicyRenewal: '#1890ff',

    // Claim Status Colors
    colorClaimOpen: '#1890ff',
    colorClaimInvestigating: '#faad14',
    colorClaimApproved: '#52c41a',
    colorClaimSettled: '#389e0d',
    colorClaimRepudiated: '#ff4d4f',
    colorClaimClosed: '#8c8c8c',

    // Risk Level Colors
    colorRiskLow: '#52c41a',
    colorRiskMedium: '#faad14',
    colorRiskHigh: '#ff7a45',
    colorRiskVeryHigh: '#ff4d4f',

    // Coverage Type Colors
    colorCoverageComprehensive: '#1890ff',
    colorCoverageThirdParty: '#597ef7',
    colorCoverageFireTheft: '#fa8c16',

    // Payment Status Colors
    colorPaymentPending: '#faad14',
    colorPaymentProcessing: '#1890ff',
    colorPaymentCompleted: '#52c41a',
    colorPaymentFailed: '#ff4d4f',
    colorPaymentRefunded: '#13c2c2',
};

// Dark mode variants
export const insuranceColorsDark = {
    // Quote Status Colors - adjusted for dark mode
    colorQuoteNew: '#177ddc',
    colorQuoteReferred: '#d89614',
    colorQuoteApproved: '#49aa19',
    colorQuoteDeclined: '#d32029',
    colorQuoteExpired: '#434343',
    colorQuoteConverted: '#13a8a8',

    // Policy Status Colors - adjusted for dark mode
    colorPolicyActive: '#49aa19',
    colorPolicyLapsed: '#d89614',
    colorPolicyCancelled: '#d32029',
    colorPolicyExpired: '#434343',
    colorPolicySuspended: '#d87a16',
    colorPolicyRenewal: '#177ddc',

    // Claim Status Colors - adjusted for dark mode
    colorClaimOpen: '#177ddc',
    colorClaimInvestigating: '#d89614',
    colorClaimApproved: '#49aa19',
    colorClaimSettled: '#389e0d',
    colorClaimRepudiated: '#d32029',
    colorClaimClosed: '#595959',

    // Risk Level Colors - adjusted for dark mode
    colorRiskLow: '#49aa19',
    colorRiskMedium: '#d89614',
    colorRiskHigh: '#d46b08',
    colorRiskVeryHigh: '#d32029',

    // Coverage Type Colors - adjusted for dark mode
    colorCoverageComprehensive: '#177ddc',
    colorCoverageThirdParty: '#3c5ecc',
    colorCoverageFireTheft: '#d87a16',

    // Payment Status Colors - adjusted for dark mode
    colorPaymentPending: '#d89614',
    colorPaymentProcessing: '#177ddc',
    colorPaymentCompleted: '#49aa19',
    colorPaymentFailed: '#d32029',
    colorPaymentRefunded: '#13a8a8',
};

// Export combined theme tokens for easy access
export const insuranceThemeTokens = {
    ...insuranceColors,
    colorPrimaryInsurance: '#1890ff',
};

// Get insurance theme tokens based on current theme
export const getInsuranceTheme = (isDark: boolean = false) => {
    const colors = isDark ? insuranceColorsDark : insuranceColors;

    return {
        token: {
            // Extend existing Ant Design tokens
            ...colors,

            // Component-specific tokens
            cardShadow: isDark
                ? '0 2px 8px rgba(0, 0, 0, 0.25)'
                : '0 2px 8px rgba(0, 0, 0, 0.08)',
            cardHoverShadow: isDark
                ? '0 4px 16px rgba(0, 0, 0, 0.35)'
                : '0 4px 16px rgba(0, 0, 0, 0.12)',

            // Animation tokens
            motionDurationFast: '100ms',
            motionDurationMid: '200ms',
            motionDurationSlow: '300ms',
            motionEaseInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
            motionEaseOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
            motionEaseIn: 'cubic-bezier(0.4, 0, 1, 1)',

            // Insurance-specific spacing
            insuranceSpacingXS: 4,
            insuranceSpacingSM: 8,
            insuranceSpacingMD: 16,
            insuranceSpacingLG: 24,
            insuranceSpacingXL: 32,
        }
    };
};

// Status badge mapping
export const statusColorMap = {
    // Quote statuses
    'new': insuranceColors.colorQuoteNew,
    'referred': insuranceColors.colorQuoteReferred,
    'approved': insuranceColors.colorQuoteApproved,
    'declined': insuranceColors.colorQuoteDeclined,
    'expired': insuranceColors.colorQuoteExpired,
    'converted': insuranceColors.colorQuoteConverted,

    // Policy statuses
    'active': insuranceColors.colorPolicyActive,
    'lapsed': insuranceColors.colorPolicyLapsed,
    'cancelled': insuranceColors.colorPolicyCancelled,
    'suspended': insuranceColors.colorPolicySuspended,

    // Claim statuses
    'open': insuranceColors.colorClaimOpen,
    'investigating': insuranceColors.colorClaimInvestigating,
    'settled': insuranceColors.colorClaimSettled,
    'repudiated': insuranceColors.colorClaimRepudiated,
    'closed': insuranceColors.colorClaimClosed,

    // Payment statuses
    'pending': insuranceColors.colorPaymentPending,
    'processing': insuranceColors.colorPaymentProcessing,
    'completed': insuranceColors.colorPaymentCompleted,
    'failed': insuranceColors.colorPaymentFailed,
    'refunded': insuranceColors.colorPaymentRefunded,
};

// Dark mode status badge mapping
export const statusColorMapDark = {
    // Quote statuses
    'new': insuranceColorsDark.colorQuoteNew,
    'referred': insuranceColorsDark.colorQuoteReferred,
    'approved': insuranceColorsDark.colorQuoteApproved,
    'declined': insuranceColorsDark.colorQuoteDeclined,
    'expired': insuranceColorsDark.colorQuoteExpired,
    'converted': insuranceColorsDark.colorQuoteConverted,

    // Policy statuses
    'active': insuranceColorsDark.colorPolicyActive,
    'lapsed': insuranceColorsDark.colorPolicyLapsed,
    'cancelled': insuranceColorsDark.colorPolicyCancelled,
    'suspended': insuranceColorsDark.colorPolicySuspended,

    // Claim statuses
    'open': insuranceColorsDark.colorClaimOpen,
    'investigating': insuranceColorsDark.colorClaimInvestigating,
    'settled': insuranceColorsDark.colorClaimSettled,
    'repudiated': insuranceColorsDark.colorClaimRepudiated,
    'closed': insuranceColorsDark.colorClaimClosed,

    // Payment statuses
    'pending': insuranceColorsDark.colorPaymentPending,
    'processing': insuranceColorsDark.colorPaymentProcessing,
    'completed': insuranceColorsDark.colorPaymentCompleted,
    'failed': insuranceColorsDark.colorPaymentFailed,
    'refunded': insuranceColorsDark.colorPaymentRefunded,
};

// Get status color based on theme
export const getStatusColor = (status: string, isDark: boolean = false): string => {
    const colorMap = isDark ? statusColorMapDark : statusColorMap;
    return colorMap[status.toLowerCase()] || (isDark ? '#595959' : '#d9d9d9');
};

// Risk level colors
export const getRiskColor = (level: 'low' | 'medium' | 'high' | 'very-high', isDark: boolean = false): string => {
    const colors = isDark ? insuranceColorsDark : insuranceColors;
    const riskMap = {
        'low': colors.colorRiskLow,
        'medium': colors.colorRiskMedium,
        'high': colors.colorRiskHigh,
        'very-high': colors.colorRiskVeryHigh,
    };
    return riskMap[level] || colors.colorRiskMedium;
};

// Coverage type colors
export const getCoverageColor = (type: string, isDark: boolean = false): string => {
    const colors = isDark ? insuranceColorsDark : insuranceColors;
    const coverageMap = {
        'comprehensive': colors.colorCoverageComprehensive,
        'third_party': colors.colorCoverageThirdParty,
        'third_party_fire_theft': colors.colorCoverageFireTheft,
    };
    return coverageMap[type.toLowerCase()] || colors.colorCoverageComprehensive;
};

// Animation presets following Koodooka patterns
export const insuranceAnimations = {
    // Page transitions
    pageEnter: {
        initial: { opacity: 0, x: 20 },
        animate: { opacity: 1, x: 0 },
        exit: { opacity: 0, x: -20 },
        transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] }
    },

    // Card animations
    cardHover: {
        scale: 1.02,
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
        transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] }
    },

    // Form validation
    shake: {
        x: [0, -10, 10, -10, 10, 0],
        transition: { duration: 0.4 }
    },

    // Success animation
    success: {
        scale: [0.8, 1.1, 1],
        opacity: [0, 1, 1],
        transition: { duration: 0.3 }
    },

    // Loading skeleton
    skeleton: {
        background: [
            'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            'linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%)',
            'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
        ],
        transition: {
            duration: 1.5,
            repeat: Infinity,
            ease: 'linear'
        }
    },

    // Number counting
    counter: {
        transition: {
            duration: 1,
            ease: [0.4, 0, 0.2, 1]
        }
    }
};

// Export theme integration helper
export const applyInsuranceTheme = (isDark: boolean = false) => {
    const insuranceTheme = getInsuranceTheme(isDark);

    // Return theme configuration for Ant Design ConfigProvider
    return {
        algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
        token: {
            ...insuranceTheme.token,
            // Ensure consistency with Koodooka base theme
            borderRadius: 8,
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        }
    };
};

// Utility function to merge with existing Koodooka theme
export const mergeWithKoodookaTheme = (koodookaTheme: any, isDark: boolean = false) => {
    const insuranceTheme = getInsuranceTheme(isDark);

    return {
        ...koodookaTheme,
        token: {
            ...koodookaTheme.token,
            ...insuranceTheme.token,
        }
    };
};
